import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Award, Calendar, Video } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BowerLecturesPage = () => {
  const pastSpeakers = [
    "Prof. Arkotong Longkumer (University of Edinburgh)",
    "Dr. Mark Elliott (Museum of Archaeology and Anthropology, University of Cambridge)",
    "Fr. Dr. Abraham Lotha (The Chumpo Museum)",
    "Prof. Peter J. Matthews (Kyoto University)",
    "Dr. Bérénice Guyot-Rechard (King's College London)",
    "Prof. Antonio Guerreiro (State University of Campinas, Brazil)",
    "Dr. Dharamsing Teron (Centre for Karbi Studies)",
    "Dr. Lydia Walker (Ohio State University)",
    "Prof. William Gould (University of Leeds)",
    "Dr. Suryasikha Pathak (Dibrugarh University)",
    "Ms. Catriona G. Child (The Highland Institute)"
  ];

  const purposes = [
    {
      icon: <Award className="w-6 h-6" />,
      title: "Celebrate Scholarship",
      description: "Leading-edge research in anthropology, indigenous studies, environmental humanities, and history"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Interrogate Issues",
      description: "Urgent challenges affecting highland and borderland communities across Asia and globally"
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Mentor Scholars",
      description: "Integration with Winter Research Colloquium and PGCertR programme"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Advance Dialogue",
      description: "Critical conversations on decolonisation, representation, and plural ways of knowing"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_institute-clone/artifacts/246fk2u6_Conference%20Highland%20Institute.jpg)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              The Ursula Graham Bower Lectures
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              An Annual Lecture Series in Honour of a Pioneering Anthropologist
            </p>
          </div>
        </div>
      </section>

      {/* About the Lecture Series */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">About the Lecture Series</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The <strong>Ursula Graham Bower Lectures</strong> are the Highland Institute's most prestigious annual 
                academic event, established to honour the legacy of one of the twentieth century's most remarkable 
                ethnographers and wartime leaders. Known for her deep and sustained engagement with the Zeme Nagas of 
                Northeast India, Ursula Graham Bower produced a formidable body of ethnographic writing, photography, 
                and film during the 1930s and 1940s.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                During World War II, she led a guerrilla force of Naga scouts—famously known as <em>Bower Force</em>—in 
                resistance to the Japanese advance into India. Her life and work exemplify fearless inquiry, deep cultural 
                empathy, and a commitment to ethical engagement in politically charged and often dangerous contexts.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The lecture series traces its origins to 2013, when it was inaugurated as the Hutton Lectures. In 2021, 
                responding to the evolving mission of the Institute and global debates on decolonising knowledge, the 
                series was reconstituted as the Highlander Lectures. Finally, in 2025, the event was renamed the 
                <strong> Ursula Graham Bower Lectures</strong>, anchoring the series in the legacy of a woman whose 
                anthropological vision, wartime courage, and cross-cultural solidarity embody the values the Institute 
                holds most dear.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Purpose and Format */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Purpose and Format</h2>
            <p className="text-gray-700 text-center mb-12 max-w-3xl mx-auto">
              Held annually in December in Kohima, Nagaland, the Bower Lectures form the centrepiece of the Institute's 
              academic calendar. Each year, a distinguished scholar is invited to deliver the keynote lecture.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {purposes.map((purpose, index) => (
                <Card key={index} className="hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                      {purpose.icon}
                    </div>
                    <CardTitle className="text-lg">{purpose.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{purpose.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Lecture Symposium */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">The Lecture Symposium</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                While the keynote lecture remains the centrepiece, the event is typically accompanied by a 
                <strong> Lecture Symposium</strong>, a multi-day academic gathering. From its earliest days as the 
                Hutton Lectures, the vision behind the series included a symposium component—an open forum for 
                researchers working across the region to present ongoing work, share insights, and collectively 
                reflect on the year's research activities.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                From 2025 onward, the symposium is formally integrated into the Postgraduate Certificate in Research 
                (PGCertR) programme. PGCertR students present their preliminary research findings in the Winter Research 
                Colloquium, contributing to a dynamic academic space where senior scholars, mentors, and community 
                partners offer feedback and support.
              </p>
              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">The Symposium Features:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">British Academy Writing Workshops (2021–2022)</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Project Conferences, such as the Ekologos: Global Environmental Humanities Conference (2023)</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Film screenings, artist talks, roundtables, and archival showcases</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Distinguished Past Speakers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Distinguished Past Speakers</h2>
            <p className="text-gray-700 text-center mb-8">
              Since 2013, the lecture series has welcomed leading thinkers and practitioners from around the world:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pastSpeakers.map((speaker, index) => (
                <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                  <Award className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">{speaker}</span>
                </div>
              ))}
            </div>
            <p className="text-gray-600 text-center mt-8">
              Lecture topics have ranged widely—from indigenous theology, oral literature, and borderland politics, 
              to climate change, religious nationalism, and museology.
            </p>
          </div>
        </div>
      </section>

      {/* Legacy of Ursula Graham Bower */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="md:col-span-1">
                <img 
                  src="https://highlandinstitute.org/wp-content/uploads/2025/03/Ursula-bower.jpg" 
                  alt="Ursula Graham Bower"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <div className="md:col-span-2">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Legacy of Ursula Graham Bower</h2>
                <div className="prose prose-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    <strong>Ursula Graham Bower (1914–1988)</strong> is remembered as a trailblazing anthropologist, 
                    documentarian, and wartime leader. Educated at Roedean School in England, she first travelled to 
                    Northeast India in 1937 and immersed herself in the lives of the Zeme Nagas.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Over nearly a decade, she produced a substantial ethnographic archive—photographs, films, notebooks, 
                    and the celebrated books <em>Naga Path</em> and <em>The Hidden Land</em>.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    During World War II, Bower led <em>Bower Force</em>, a group of Naga scouts under V Force, in 
                    defence of India's eastern frontier. For her courage, she was awarded the MBE and the Lawrence of 
                    Arabia Medal. Her legacy—of intellectual rigour, deep humility, and extraordinary bravery—continues 
                    to shape the Institute's ethos and aspirations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Publications and Recordings */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Video className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Publications and Recordings</h2>
            <p className="text-gray-700 mb-8">
              Select lectures are recorded and archived in the Highland Institute Video Library. Written excerpts and 
              curated reflections are periodically published via Highlander Press, and integrated into volumes such as 
              <em> Passing Things On: Ancestors and Genealogies in Northeast India</em> (2014). A future collected 
              edition of Bower Lectures is in development.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Attend or Participate</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            The next edition of the Ursula Graham Bower Lectures will be held in December 2025 in Kohima, Nagaland.
          </p>
          <p className="text-lg mb-8">
            For updates or to express interest in participating in the Lecture Symposium, please contact the Academic Office.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/events">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                View All Events
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BowerLecturesPage;
